import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MdErrorOutline } from 'react-icons/md';
import { Button, PromoBanner, Spinner, PROMO_BANNER_TONE_ERROR } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import type { AppRunDetail } from '@api/apps';
import { FeatureGate, useFeatureFlagsReady, useFeatureGate } from '@featureFlags/index';
import { useUserAppDraft } from './builder/useAppBuilder';
import { builderRunPath } from './builder/userAppStatus';
import AppFrame from './appFrame/AppFrame';
import AppWindow, { WindowSurface } from './appFrame/AppWindow';
import KitUpdateNotice from './appFrame/KitUpdateNotice';
import { framePage } from './appFrame/framePage';
import { useAppRunController } from './appFrame/useAppRunController';
import { useRouteRunHost } from './appFrame/useRouteRunHost';
import { useFullscreen } from './appFrame/useFullscreen';
import { useRunCaptureView } from './appFrame/useRunCaptureView';
import { usesAgent } from './appCapabilities';
import { localized } from './appRunView';
import AppRunHeader from './run/AppRunHeader';
import AppRunExecutionPanel from './runSetup/AppRunExecutionPanel';
import ResumeRunBanner from './ResumeRunBanner';
import { useAppRun } from './useApps';
import { ViewSnapshotProvider } from './viewSnapshot';
import { PageBody, PageRoot } from './AppRunPage.styled';

const AppRunScreen: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { appKey, runId } = useParams<{ appKey: string; runId?: string }>();
  const host = useRouteRunHost(appKey, runId);
  const controller = useAppRunController(host);
  const { run, manifest } = controller;
  const { catalogEntry } = host;
  const { rootRef, captureView } = useRunCaptureView('app_run', appKey, run);
  const [fullscreenRef, fullscreen] = useFullscreen<HTMLDivElement>();

  if (!manifest && controller.isLoading) {
    return (
      <PageRoot>
        <PageBody>
          <Spinner label={intl.formatMessage({ id: 'apps.run.loading', defaultMessage: 'Loading this run…' })} />
        </PageBody>
      </PageRoot>
    );
  }

  if (!manifest || !appKey || controller.notFound) {
    return (
      <PageRoot>
        <PageBody>
          <PromoBanner
            tone={PROMO_BANNER_TONE_ERROR}
            icon={<MdErrorOutline />}
            title={
              runId
                ? intl.formatMessage({ id: 'apps.run.notFound', defaultMessage: 'This run could not be opened. It may have been deleted.' })
                : intl.formatMessage({
                    id: 'apps.run.appUnavailable',
                    defaultMessage: 'This app is not available. It may have been removed or is no longer shared with you.',
                  })
            }
            actions={
              <Button variant="text" size="sm" onClick={() => navigate('/apps')}>
                {intl.formatMessage({ id: 'apps.builder.back', defaultMessage: 'Back to apps' })}
              </Button>
            }
            dataTestId="app-run__unavailable"
          />
        </PageBody>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <PageBody ref={rootRef}>
        {catalogEntry?.ownedByMe && <KitUpdateNotice appKey={appKey} manifest={catalogEntry.manifest} />}
        {usesAgent(manifest) && <ResumeRunBanner controller={controller} />}
        <AppWindow
          windowRef={fullscreenRef}
          bar={<AppRunHeader controller={controller} manifest={manifest} catalogEntry={catalogEntry} fullscreen={fullscreen} />}
          dataTestId="app-fullscreen"
        >
          {usesAgent(manifest) && <AppRunExecutionPanel controller={controller} />}
          <WindowSurface>
            <AppFrame
              manifest={manifest}
              page={framePage(manifest, appKey, run?.runId, 'catalog')}
              controller={controller}
              title={localized(intl, manifest.name)}
              captureView={captureView}
              untrusted={controller.crossOrg}
            />
          </WindowSurface>
        </AppWindow>
      </PageBody>
    </PageRoot>
  );
};

// The builder shows only a run of the page its draft has now; any other test run stays here.
const builderCanShow = (run: AppRunDetail, draft: UserAppDraft): boolean => {
  const page = draft.preview.presentation?.page;
  return draft.status !== 'DISABLED' && Boolean(page) && run.manifest.presentation?.page === page;
};

/** An owner's test run the builder can show goes there before anything attaches to its stream. */
const AppRunPage: React.FC = () => {
  const { runId } = useParams<{ runId?: string }>();
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const gatesReady = useFeatureFlagsReady();
  const { data: run, isLoading } = useAppRun(runId);
  const [screenShown, setScreenShown] = useState(false);
  const ownTestRun = Boolean(run?.isTest && !run.readOnly);
  const draft = useUserAppDraft(run?.appKey, ownTestRun && builderEnabled);
  const draftPending = ownTestRun && (!gatesReady || (builderEnabled && !draft.isSuccess && !draft.isError));
  // Only the first screen waits: a run opened from it must keep the same frame and controller, which hold its uploads.
  const deciding = !screenShown && Boolean(runId) && (isLoading || draftPending);
  const toBuilder = Boolean(run && ownTestRun && builderEnabled && draft.data && builderCanShow(run, draft.data));
  const showsScreen = !deciding && !toBuilder;
  if (showsScreen && !screenShown) setScreenShown(true);

  if (deciding) {
    return (
      <PageRoot>
        <PageBody>
          <Spinner label="" />
        </PageBody>
      </PageRoot>
    );
  }
  if (run && toBuilder) return <Navigate replace to={builderRunPath(run.appKey, run.runId)} />;
  return (
    <ViewSnapshotProvider>
      <AppRunScreen />
    </ViewSnapshotProvider>
  );
};

export default AppRunPage;
